import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { CardSurface } from "@/components/card/CardSurface";
import { adminChangeOrderStatus, adminGetOrderDetail } from "@/lib/adminApi";
import axios from "axios";
import { ADMIN_ORDER_DETAIL } from "@/constants/testIds/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  draft: { label: "Draf", cls: "gray" },
  pending_payment: { label: "Menunggu Bayaran", cls: "amber" },
  payment_confirmed: { label: "Bayaran Disahkan", cls: "teal" },
  processing: { label: "Sedang Diproses", cls: "blue" },
  completed: { label: "Selesai", cls: "green" },
  cancelled: { label: "Dibatalkan", cls: "red" },
};
const PAYMENT_STATUS = {
  unpaid: { label: "Belum Bayar", cls: "amber" },
  pending: { label: "Menunggu", cls: "amber" },
  paid: { label: "Telah Bayar", cls: "green" },
  failed: { label: "Gagal", cls: "red" },
  expired: { label: "Tamat Tempoh", cls: "gray" },
  refunded: { label: "Dikembalikan", cls: "gray" },
};
const ARTWORK_STATUS = {
  not_generated: { label: "Belum Dijana", cls: "gray" },
  pending: { label: "Menunggu", cls: "amber" },
  generated: { label: "Telah Dijana", cls: "green" },
  approved: { label: "Diluluskan", cls: "green" },
  failed: { label: "Gagal", cls: "red" },
};
const DIRECTORY_STATUS = {
  not_submitted: { label: "Belum Dihantar", cls: "gray" },
  pending: { label: "Menunggu", cls: "amber" },
  submitted: { label: "Telah Dihantar", cls: "teal" },
  listed: { label: "Tersenarai", cls: "green" },
  failed: { label: "Gagal", cls: "red" },
};

const BADGE_CLS = {
  amber: "bg-amber-400/10 text-amber-300 ring-amber-400/30",
  teal: "bg-teal-400/10 text-teal-300 ring-teal-400/30",
  blue: "bg-sky-400/10 text-sky-300 ring-sky-400/30",
  green: "bg-emerald-400/10 text-emerald-300 ring-emerald-400/30",
  red: "bg-rose-400/10 text-rose-300 ring-rose-400/30",
  gray: "bg-white/5 text-[#a9c4b8] ring-white/15",
};

const Badge = ({ map, value }) => {
  const item = map[value] || { label: value || "—", cls: "gray" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${BADGE_CLS[item.cls]}`}
    >
      {item.label}
    </span>
  );
};

const money = (v) =>
  `RM${Number(v || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const Row = ({ label, value }) =>
  value ? (
    <div className="flex justify-between gap-4 py-1.5 border-b border-white/[0.06] last:border-0">
      <span className="text-[#a9c4b8] shrink-0">{label}</span>
      <span className="text-white text-right break-words">{value}</span>
    </div>
  ) : null;

const Section = ({ title, children }) => (
  <section className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5">
    <h2 className="font-heading text-base font-bold text-white">{title}</h2>
    <div className="mt-3 text-sm">{children}</div>
  </section>
);

const addrText = (a) =>
  a
    ? [a.line1, a.line2, `${a.postcode || ""} ${a.city || ""}`.trim(), a.state, a.country]
        .filter(Boolean)
        .join(", ")
    : "—";

// Map backend order (snake_case) -> CardTemplate data shape (camelCase).
const toCardData = (o) => ({
  business: {
    namaPenuh: o.business_information.nama_penuh,
    jawatan: o.business_information.jawatan,
    namaPerniagaan: o.business_information.nama_perniagaan,
    bidangBisnes: o.business_information.bidang_bisnes,
    telefon: o.business_information.telefon,
    whatsapp: o.business_information.whatsapp,
    email: o.business_information.email,
    website: o.business_information.website,
  },
  businessAddress: {
    line1: o.business_address.line1,
    city: o.business_address.city,
    state: o.business_address.state,
  },
});

export default function AdminOrderDetailPage() {
  const { orderReference } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [side, setSide] = useState("front");
  const [busyStatus, setBusyStatus] = useState(null);
  const [statusError, setStatusError] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [artworkBusy, setArtworkBusy] = useState(false);
  const [artworkError, setArtworkError] = useState("");
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const d = await adminGetOrderDetail(orderReference);
      setOrder(d);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [orderReference, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (newStatus) => {
    setBusyStatus(newStatus);
    setStatusError("");
    try {
      const updated = await adminChangeOrderStatus(orderReference, newStatus);
      setOrder(updated);
      setConfirmCancel(false);
      toast.success("Status tempahan dikemas kini.");
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      const detail = err?.response?.data?.detail;
      setStatusError(
        typeof detail === "string"
          ? detail
          : "Perubahan status gagal. Sila cuba lagi.",
      );
    } finally {
      setBusyStatus(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1f18]">
        <AdminHeader />
        <div
          className="grid place-items-center py-24"
          data-testid={ADMIN_ORDER_DETAIL.loading}
        >
          <Loader2 className="h-6 w-6 animate-spin text-[#c79a3b]" strokeWidth={2} />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#0b1f18] text-white">
        <AdminHeader />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div
            className="rounded-2xl bg-[#3a1512] ring-1 ring-[#ff6b6b]/30 p-6"
            data-testid={ADMIN_ORDER_DETAIL.error}
          >
            <p className="text-sm text-[#ffb4a2]">
              Tempahan tidak dijumpai atau gagal dimuatkan.
            </p>
          </div>
          <Link
            to="/admin/orders"
            className="mt-6 inline-flex items-center gap-2 text-sm text-[#a9c4b8] hover:text-white"
          >
            <ArrowLeft strokeWidth={1.75} className="h-4 w-4" />
            Kembali ke senarai
          </Link>
        </div>
      </div>
    );
  }

  const finalizeArtwork = async () => {
    if (finalizeBusy) return;
    setFinalizeBusy(true);
    setArtworkError("");
    try {
      const rep = await axios.post(
        `${API}/admin/orders/${encodeURIComponent(orderReference)}/artwork/finalize`,
        {},
        { withCredentials: true },
      );
      toast[rep.data.status === "PASS" ? "success" : "error"](
        rep.data.status === "PASS"
          ? "Preflight LULUS — PDF Excard sedia."
          : "Preflight GAGAL — sila semak laporan.",
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setArtworkError(typeof detail === "string" ? detail : "Preflight gagal.");
    } finally {
      setFinalizeBusy(false);
    }
  };

  const approveArtwork = async () => {
    if (approveBusy) return;
    setApproveBusy(true);
    setArtworkError("");
    try {
      await axios.post(
        `${API}/admin/orders/${encodeURIComponent(orderReference)}/artwork/approve`,
        {},
        { withCredentials: true },
      );
      toast.success("Artwork telah diluluskan.");
      setConfirmApprove(false);
      await load();
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      const detail = err?.response?.data?.detail;
      setArtworkError(
        typeof detail === "string" ? detail : "Kelulusan artwork gagal.",
      );
    } finally {
      setApproveBusy(false);
    }
  };

  const nonCancel = order.allowed_transitions.filter((s) => s !== "cancelled");
  const canCancel = order.allowed_transitions.includes("cancelled");
  const cardData = toCardData(order);

  const generateArtwork = async () => {
    if (artworkBusy) return;
    setArtworkBusy(true);
    setArtworkError("");
    try {
      await axios.post(
        `${API}/admin/orders/${encodeURIComponent(orderReference)}/artwork/generate`,
        {},
        { withCredentials: true },
      );
      toast.success("Artwork produksi berjaya dijana.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setArtworkError(
        typeof detail === "string" ? detail : "Penjanaan artwork gagal.",
      );
    } finally {
      setArtworkBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0b1f18] text-white"
      data-testid={ADMIN_ORDER_DETAIL.page}
    >
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Link
          to="/admin/orders"
          data-testid={ADMIN_ORDER_DETAIL.back}
          className="inline-flex items-center gap-2 text-sm text-[#a9c4b8] hover:text-white transition"
        >
          <ArrowLeft strokeWidth={1.75} className="h-4 w-4" />
          Kembali ke Tempahan
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-extrabold font-mono text-[#e0b455]">
              {order.order_reference}
            </h1>
            <p className="mt-1 text-sm text-[#a9c4b8]">
              Dicatat pada {formatDate(order.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge map={ORDER_STATUS} value={order.order_status} />
            <Badge map={PAYMENT_STATUS} value={order.payment_status} />
            <Badge map={ARTWORK_STATUS} value={order.artwork_status} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card preview */}
          <Section title="Preview Kad">
            <div className="flex gap-2 mb-4">
              {["front", "back"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSide(s)}
                  className={`flex-1 h-10 rounded-xl text-sm font-semibold transition ${
                    side === s
                      ? "bg-gradient-to-b from-[#12876a] to-[#0f3f2f] text-white"
                      : "bg-white/5 text-[#a9c4b8] hover:text-white ring-1 ring-white/10"
                  }`}
                >
                  {s === "front" ? "DEPAN" : "BELAKANG"}
                </button>
              ))}
            </div>
            <div className="grid gap-4">
              <div
                className={side === "front" ? "" : "hidden"}
                data-testid={ADMIN_ORDER_DETAIL.cardFront}
              >
                <CardSurface
                  styleKey={order.style_key}
                  side="front"
                  data={cardData}
                  logo={order.has_logo ? `${BACKEND}${order.logo_url}` : null}
                  className="mx-auto"
                />
              </div>
              <div
                className={side === "back" ? "" : "hidden"}
                data-testid={ADMIN_ORDER_DETAIL.cardBack}
              >
                <CardSurface
                  styleKey={order.style_key}
                  side="back"
                  data={cardData}
                  logo={order.has_logo ? `${BACKEND}${order.logo_url}` : null}
                  className="mx-auto"
                />
              </div>
            </div>
            <p className="mt-4 text-xs text-[#c79a3b] font-semibold">
              Preview Skrin — Bukan Artwork Cetakan
            </p>
          </Section>

          {/* Logo */}
          <Section title="Logo Pelanggan">
            {order.has_logo ? (
              <div className="flex items-center justify-center rounded-xl bg-white p-6">
                <img
                  src={`${BACKEND}${order.logo_url}`}
                  alt="Logo pelanggan"
                  className="max-h-40 max-w-full object-contain"
                  data-testid={ADMIN_ORDER_DETAIL.logo}
                />
              </div>
            ) : (
              <div
                className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-6 text-center text-sm text-[#a9c4b8]"
                data-testid={ADMIN_ORDER_DETAIL.noLogo}
              >
                {order.logo_choice === "not_yet"
                  ? "Pelanggan akan hantar logo kemudian."
                  : "Tiada logo untuk tempahan ini."}
              </div>
            )}
          </Section>

          {/* Customer / business */}
          <Section title="Maklumat Pelanggan & Perniagaan">
            <Row label="Nama Penuh" value={order.business_information.nama_penuh} />
            <Row label="Jawatan" value={order.business_information.jawatan} />
            <Row label="Nama Perniagaan" value={order.business_information.nama_perniagaan} />
            <Row label="Bidang Bisnes" value={order.business_information.bidang_bisnes} />
            <Row label="Telefon" value={order.business_information.telefon} />
            <Row label="WhatsApp" value={order.business_information.whatsapp} />
            <Row label="E-mel" value={order.business_information.email} />
            <Row label="Website" value={order.business_information.website} />
          </Section>

          {/* Product / design */}
          <Section title="Design & Spesifikasi Produk">
            <Row label="Design" value={`${order.template_name} (${order.template_id})`} />
            <Row label="Produk" value={order.product_name} />
            <Row label="Bahan" value={order.material} />
            <Row label="GSM" value={String(order.gsm)} />
            <Row label="Finishing" value={order.finishing} />
            <Row label="Cetakan" value={order.printing} />
            <Row label="Saiz Kad" value={order.card_size} />
            <Row label="Orientasi" value={order.orientation} />
            <Row label="Kuantiti" value={`${order.quantity} keping`} />
          </Section>

          {/* Addresses */}
          <Section title="Alamat Perniagaan">
            <p className="text-white leading-relaxed">{addrText(order.business_address)}</p>
          </Section>
          <Section title="Alamat Penghantaran">
            <p className="text-white leading-relaxed">
              {order.shipping_same_as_business
                ? "Sama seperti alamat perniagaan."
                : addrText(order.shipping_address)}
            </p>
          </Section>

          {/* Pricing */}
          <Section title="Harga (Direkodkan)">
            <Row label="Harga Produk" value={money(order.product_amount)} />
            <Row label="Penghantaran" value={money(order.shipping_amount)} />
            <div className="flex justify-between gap-4 pt-3 mt-1 border-t border-white/10">
              <span className="font-semibold text-white">Jumlah Keseluruhan</span>
              <span className="font-bold text-[#e0b455]">{money(order.grand_total)}</span>
            </div>
          </Section>

          {/* Status */}
          <Section title="Status">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[#a9c4b8]">Status Tempahan</span>
                <Badge map={ORDER_STATUS} value={order.order_status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#a9c4b8]">
                  Bayaran <span className="text-xs">(baca sahaja)</span>
                </span>
                <Badge map={PAYMENT_STATUS} value={order.payment_status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#a9c4b8]">Artwork</span>
                <Badge map={ARTWORK_STATUS} value={order.artwork_status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#a9c4b8]">DirektoriBiz</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-[#a9c4b8]">
                    {order.directory_listing_consent ? "Setuju" : "Tidak"}
                  </span>
                  <Badge map={DIRECTORY_STATUS} value={order.directory_listing_status} />
                </span>
              </div>
            </div>
          </Section>
        </div>

        {/* Status management */}
        <Section title="Urus Status Tempahan">
          {order.allowed_transitions.length === 0 ? (
            <p className="text-sm text-[#a9c4b8]">
              Tiada perubahan status tersedia untuk tempahan ini.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {nonCancel.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={!!busyStatus}
                    onClick={() => changeStatus(s)}
                    data-testid={`${ADMIN_ORDER_DETAIL.transitionButton}-${s}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#e0b455] to-[#c79a3b] text-[#0b1f18] font-bold px-4 py-2.5 text-sm transition hover:brightness-105 disabled:opacity-60"
                  >
                    {busyStatus === s && (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                    )}
                    Tandakan: {ORDER_STATUS[s]?.label || s}
                  </button>
                ))}
              </div>

              {canCancel && (
                <div className="pt-2 border-t border-white/10">
                  {!confirmCancel ? (
                    <button
                      type="button"
                      onClick={() => setConfirmCancel(true)}
                      data-testid={ADMIN_ORDER_DETAIL.cancelButton}
                      className="inline-flex items-center gap-2 rounded-xl ring-1 ring-rose-400/40 text-rose-300 px-4 py-2.5 text-sm hover:bg-rose-400/10 transition"
                    >
                      <Ban strokeWidth={1.75} className="h-4 w-4" />
                      Batalkan Tempahan
                    </button>
                  ) : (
                    <div className="rounded-xl bg-[#3a1512] ring-1 ring-rose-400/30 p-4">
                      <p className="text-sm text-[#ffb4a2]">
                        Batalkan tempahan ini? Tindakan ini tidak boleh dibuat asal.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          disabled={!!busyStatus}
                          onClick={() => changeStatus("cancelled")}
                          data-testid={ADMIN_ORDER_DETAIL.cancelConfirm}
                          className="inline-flex items-center gap-2 rounded-xl bg-rose-500 text-white font-semibold px-4 py-2 text-sm hover:bg-rose-600 transition disabled:opacity-60"
                        >
                          {busyStatus === "cancelled" && (
                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                          )}
                          Ya, Batalkan
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmCancel(false)}
                          data-testid={ADMIN_ORDER_DETAIL.cancelDismiss}
                          className="rounded-xl ring-1 ring-white/15 px-4 py-2 text-sm text-[#e8f0ec] hover:bg-white/5 transition"
                        >
                          Tidak
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {statusError && (
                <p
                  className="text-sm text-[#ffb4a2]"
                  data-testid={ADMIN_ORDER_DETAIL.statusError}
                  role="alert"
                >
                  {statusError}
                </p>
              )}
            </div>
          )}
        </Section>

        {/* Artwork (Phase 2D-1 internal render) */}
        <Section title="Artwork Produksi (Dalaman)">
          {order.payment_status !== "paid" ? (
            <p className="text-sm text-[#a9c4b8]">
              Artwork hanya boleh dijana selepas pembayaran disahkan.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#a9c4b8]">Status Artwork:</span>
                <Badge map={ARTWORK_STATUS} value={order.artwork_status} />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={generateArtwork}
                  disabled={artworkBusy}
                  data-testid="admin-artwork-generate"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#12876a] to-[#0f3f2f] text-white font-semibold px-4 py-2.5 text-sm transition hover:brightness-110 disabled:opacity-60"
                >
                  {artworkBusy && (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                  )}
                  {order.has_artwork ? "Jana Semula Artwork" : "Jana Artwork"}
                </button>
                {order.has_artwork && order.artwork_url && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${order.artwork_url}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-artwork-view"
                    className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/15 px-4 py-2.5 text-sm text-[#e0b455] hover:bg-white/5 transition"
                  >
                    Lihat Artwork (PDF)
                  </a>
                )}
              </div>
              <p className="text-xs text-[#a9c4b8]">
                Render dalaman CMYK 300dpi (depan &amp; belakang, 90×56mm penuh bleed)
                untuk semakan. Bukan lagi PDF akhir Excard.
              </p>
              {order.has_artwork && (
                <div className="pt-3 border-t border-white/10 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={finalizeArtwork}
                      disabled={finalizeBusy}
                      data-testid="admin-artwork-finalize"
                      className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#e0b455] to-[#c79a3b] text-[#0b1f18] font-bold px-4 py-2.5 text-sm transition hover:brightness-105 disabled:opacity-60"
                    >
                      {finalizeBusy && (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                      )}
                      Jana PDF Akhir + Preflight
                    </button>
                    {order.excard_ready && (
                      <span
                        className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 bg-emerald-400/10 text-emerald-300 ring-emerald-400/30"
                        data-testid="admin-artwork-excard-ready"
                      >
                        EXCARD_READY
                      </span>
                    )}
                    {order.has_final_pdf && order.final_pdf_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${order.final_pdf_url}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-artwork-final-view"
                        className="inline-flex items-center gap-2 rounded-xl ring-1 ring-white/15 px-4 py-2.5 text-sm text-[#e0b455] hover:bg-white/5 transition"
                      >
                        Lihat PDF Akhir
                      </a>
                    )}
                  </div>
                  {order.preflight_report && (
                    <div
                      className="rounded-xl bg-[#0b1f18] ring-1 ring-white/10 p-4"
                      data-testid="admin-artwork-preflight-report"
                    >
                      <p className="text-sm font-semibold">
                        Preflight:{" "}
                        <span
                          className={
                            order.preflight_report.status === "PASS"
                              ? "text-emerald-300"
                              : "text-rose-300"
                          }
                          data-testid="admin-artwork-preflight-status"
                        >
                          {order.preflight_report.status}
                        </span>
                      </p>
                      <ul className="mt-2 space-y-1">
                        {order.preflight_report.checks.map((ch) => (
                          <li key={ch.name} className="flex gap-2 text-xs">
                            <span
                              className={
                                ch.status === "PASS"
                                  ? "text-emerald-300"
                                  : ch.status === "WARN"
                                    ? "text-amber-300"
                                    : "text-rose-300"
                              }
                            >
                              [{ch.status}]
                            </span>
                            <span className="text-[#c9d8d1]">
                              {ch.name} — {ch.detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {order.artwork_status === "approved" ? (
                    <div
                      className="rounded-xl bg-emerald-400/10 ring-1 ring-emerald-400/30 p-4"
                      data-testid="admin-artwork-approved-panel"
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck
                          className="h-5 w-5 text-emerald-300"
                          strokeWidth={2}
                        />
                        <p className="text-sm font-semibold text-emerald-300">
                          Artwork Diluluskan
                        </p>
                      </div>
                      {order.artwork_approved_at && (
                        <p className="mt-1 text-xs text-[#a9c4b8]">
                          Diluluskan pada {formatDate(order.artwork_approved_at)}
                          {order.artwork_approved_by
                            ? ` oleh ${order.artwork_approved_by}`
                            : ""}
                        </p>
                      )}
                    </div>
                  ) : (
                    order.can_approve_artwork &&
                    (confirmApprove ? (
                      <div
                        className="rounded-xl bg-[#0b1f18] ring-1 ring-emerald-400/30 p-4 space-y-3"
                        data-testid="admin-artwork-approve-confirm"
                      >
                        <p className="text-sm text-[#c9d8d1]">
                          Sahkan kelulusan artwork ini? Pastikan PDF akhir telah
                          disemak. Tindakan ini akan menanda artwork sebagai{" "}
                          <span className="font-semibold text-emerald-300">
                            Diluluskan
                          </span>
                          .
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={approveArtwork}
                            disabled={approveBusy}
                            data-testid="admin-artwork-approve-confirm-yes"
                            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-[#0b1f18] font-bold px-4 py-2.5 text-sm transition hover:brightness-105 disabled:opacity-60"
                          >
                            {approveBusy && (
                              <Loader2
                                className="h-4 w-4 animate-spin"
                                strokeWidth={2}
                              />
                            )}
                            Ya, Luluskan
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmApprove(false)}
                            disabled={approveBusy}
                            data-testid="admin-artwork-approve-confirm-no"
                            className="inline-flex items-center rounded-xl ring-1 ring-white/15 px-4 py-2.5 text-sm text-[#c9d8d1] hover:bg-white/5 transition disabled:opacity-60"
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmApprove(true)}
                        data-testid="admin-artwork-approve"
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 text-[#0b1f18] font-bold px-4 py-2.5 text-sm transition hover:brightness-105"
                      >
                        <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                        Semak &amp; Luluskan Artwork
                      </button>
                    ))
                  )}
                </div>
              )}
              {artworkError && (
                <p
                  className="text-sm text-[#ffb4a2]"
                  data-testid="admin-artwork-error"
                  role="alert"
                >
                  {artworkError}
                </p>
              )}
            </div>
          )}
        </Section>

        {/* Timeline */}
        <Section title="Sejarah Tempahan">
          <ol className="space-y-3" data-testid={ADMIN_ORDER_DETAIL.timeline}>
            {order.events.map((e, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-[#c79a3b] shrink-0" />
                <div>
                  <p className="text-white">
                    Status ditukar:{" "}
                    <span className="text-[#a9c4b8]">
                      {ORDER_STATUS[e.previous_status]?.label || e.previous_status}
                    </span>{" "}
                    →{" "}
                    <span className="font-semibold">
                      {ORDER_STATUS[e.new_status]?.label || e.new_status}
                    </span>
                  </p>
                  <p className="text-xs text-[#a9c4b8]">
                    {formatDate(e.timestamp)}
                    {e.admin ? ` · ${e.admin}` : ""}
                  </p>
                </div>
              </li>
            ))}
            <li className="flex gap-3">
              <span className="mt-1.5 h-2 w-2 rounded-full bg-white/30 shrink-0" />
              <div>
                <p className="text-white">Tempahan dicatat</p>
                <p className="text-xs text-[#a9c4b8]">{formatDate(order.created_at)}</p>
              </div>
            </li>
          </ol>
        </Section>
      </main>
    </div>
  );
}
